import { atKeys, isInstance, notEmpty, notNullish, parseJsonObject } from "@de-xima/xima-common-js-lang";
import { ResizeSensor } from "css-element-queries";

import type { IMonacoExtendOptions as IMonacoExtenderOptions, IMonacoJsonSchema, TConstructor } from "../Header.ts";
import { overridePrototypeMethod, resolveURL } from "../Util.ts";

import { createSnippetCompletionProvider } from "./Autocomplete.ts";
import { concatJsonSchemas, getRegisteredSchemas } from "./JsonSchema.ts";
import { getLocalStorageServiceInstance } from "./LocalStorageService.ts";
import { tsProvideFilteredGlobalCompletionItems, type TTsProvideCompletionItems } from "./MonacoExtras.ts";
import { createTypeDefThenable } from "./TypeDefinitions.ts";
import { addPlaceholderToMonacoEditor } from "./MonacoPlaceholderWidget.ts";

/** @type {Set<string>} */
const LANGUAGES_SETUP = new Set();

/**
 * @param context Monaco Current context of the editor.
 */
function addCustomI18n(context: PrimeFaces.widget.ExtMonacoEditor.SyncMonacoCodeEditorContext) {
  if (isInstance(context, PrimeFaces.widget.ExtMonacoCodeEditorInline)) {
    let i18n = context.jq.data("i18n") as unknown;
    i18n = i18n !== undefined ? i18n : (context.getInput().data("i18n") as unknown);
    i18n = i18n !== undefined ? i18n : context.getJQ().parent().data("i18n");
    try {
      const parsed = parseJsonObject(i18n);
      const monacoEnvironment = (MonacoEnvironment ?? {}) as Record<string, unknown>;
      const locale = (monacoEnvironment.Locale ?? {}) as Record<string, unknown>;
      const data = (locale.data ?? {}) as Record<string, unknown>;
      monacoEnvironment.locale = locale;
      locale.data = data;
      Object.assign({}, data, parsed);
    } catch (e) {
      console.error("Could not process monaco i18n", e);
    }
  }
}

/**
 * Extender for the monaco editor that can be configured via an options object.
 */
class MonacoExtender
  implements
    Partial<
      PrimeFaces.widget.ExtMonacoEditor.ExtenderCodeEditor<PrimeFaces.widget.ExtMonacoEditor.SyncMonacoCodeEditorContext>
    >
{
  private _callback: (() => void) | undefined;

  private _extenderOptions: IMonacoExtenderOptions;

  private _resizeSensor: ResizeSensor | undefined;

  constructor(extenderOptions?: Partial<IMonacoExtenderOptions>) {
    this._extenderOptions = Object.assign({}, MonacoExtender.getDefaultOptions(), extenderOptions ?? {});
    this._resizeSensor = undefined;
    this._callback = undefined;
  }

  afterCreate(context: PrimeFaces.widget.ExtMonacoEditor.SyncMonacoCodeEditorContext) {
    const monaco = context.getMonaco();
    const resizeSensor = this._resizeSensor;
    if (resizeSensor !== undefined) {
      resizeSensor.detach(this._callback);
    }
    if (monaco !== undefined && notEmpty(this._extenderOptions.placeholder)) {
      addPlaceholderToMonacoEditor(this._extenderOptions.placeholder, monaco);
    }
    if (this._extenderOptions.autoResize) {
      this._callback = () => {
        context.tryWithMonaco((editor) => editor.layout(), undefined);
      };
      if (isInstance(context, PrimeFaces.widget.ExtMonacoCodeEditorInline)) {
        const target = context.jq.get(0);
        if (target !== undefined) {
          this._resizeSensor = new ResizeSensor(target, this._callback);
        }
      }
    }
  }

  beforeCreate(
    context: PrimeFaces.widget.ExtMonacoEditor.SyncMonacoCodeEditorContext,
    options: import("monaco-editor").editor.IStandaloneEditorConstructionOptions,
    wasLibLoaded: boolean,
  ): import("monaco-editor").languages.ProviderResult<import("monaco-editor").editor.IEditorConstructionOptions> {
    if (wasLibLoaded) {
      LANGUAGES_SETUP.clear();
    }
    addCustomI18n(context);
    const language = options.model?.getLanguageId() ?? "";
    const languageDone = LANGUAGES_SETUP.has(language);
    const doPerform = wasLibLoaded || !languageDone || this._extenderOptions.forceSetup;
    LANGUAGES_SETUP.add(language);
    if (doPerform) {
      this._registerCompletionItemProviders(language, options);
    }
    switch (language) {
      case "javascript":
        return doPerform ? this._setupJavascript(options) : options;
      case "json":
        return this._setupJson(options);
      default:
        return options;
    }
  }

  beforeDestroy() {
    const resizeSensor = this._resizeSensor;
    if (resizeSensor !== undefined) {
      resizeSensor.detach(this._callback);
      this._resizeSensor = undefined;
    }
  }

  createEditorOverrideServices(): import("monaco-editor").editor.IEditorOverrideServices | undefined {
    return {
      storageService: getLocalStorageServiceInstance(),
    };
  }

  /**
   * @param options Options for the editor.
   * @returns The main JSON schema for the editor.
   */
  private _getMainJsonSchema(
    options: import("monaco-editor").editor.IStandaloneEditorConstructionOptions,
  ): IMonacoJsonSchema | undefined {
    if (this._extenderOptions.jsonSchema === undefined || this._extenderOptions.jsonSchema === "") {
      return undefined;
    }
    const mainUri =
      typeof this._extenderOptions.jsonSchema === "string" ? undefined : this._extenderOptions.jsonSchema.uri;

    const mainSchema =
      typeof this._extenderOptions.jsonSchema === "string"
        ? { $ref: resolveURL(this._extenderOptions.jsonSchema) }
        : (this._extenderOptions.jsonSchema.schema as unknown);

    if (mainSchema === undefined || mainSchema === "") {
      return undefined;
    }

    const parsedSchema = parseJsonObject(mainSchema);

    const fileMatch =
      typeof this._extenderOptions.jsonSchema === "string" ? undefined : this._extenderOptions.jsonSchema.fileMatch;

    if (notNullish(options.model)) {
      return {
        fileMatch: fileMatch ?? [options.model.uri.toString()],
        schema: parsedSchema,
        uri: mainUri ?? options.model.uri.toString(),
      };
    }

    return undefined;
  }

  private _registerCompletionItemProviders(
    language: string,
    options: import("monaco-editor").editor.IStandaloneEditorConstructionOptions,
  ): void {
    const factories = this._extenderOptions.completionProviders[language] ?? [];
    for (const factory of factories) {
      const provider =
        typeof factory === "string" ? createSnippetCompletionProvider({ snippetNames: [factory] }) : factory(options);
      monaco.languages.registerCompletionItemProvider("javascript", provider);
    }
  }

  /**
   * Adds the appropriate editor options for the language `JavaScript`.
   * @param options Options for the monaco editor.
   * @returns The new or modified options.
   */
  private _setupJavascript(
    options: import("monaco-editor").editor.IEditorConstructionOptions,
  ): import("monaco-editor").languages.ProviderResult<import("monaco-editor").editor.IEditorConstructionOptions> {
    // Suggestions for form fields within the designer
    if (this._extenderOptions.limitGlobalSuggestions) {
      overridePrototypeMethod<{ provideCompletionItems: TTsProvideCompletionItems }, "provideCompletionItems">(
        atKeys(monacoExtras, ["vs", "language", "typescript", "TsMode", "SuggestAdapter"]) as TConstructor<{
          provideCompletionItems: TTsProvideCompletionItems;
        }>,
        "provideCompletionItems",
        tsProvideFilteredGlobalCompletionItems,
      );
    }

    // If given, load the type definition files into the editor.
    if (this._extenderOptions.types.length > 0) {
      const promise = createTypeDefThenable(this._extenderOptions.types);
      if (promise !== undefined) {
        return promise.then(() => options);
      }

      return options;
    }

    return options;
  }

  private _setupJson(
    options: import("monaco-editor").editor.IStandaloneEditorConstructionOptions,
  ): import("monaco-editor").languages.ProviderResult<import("monaco-editor").editor.IEditorConstructionOptions> {
    const mainSchema = this._getMainJsonSchema(options);

    const schemas = concatJsonSchemas(
      monaco.languages.json.jsonDefaults.diagnosticsOptions.schemas ?? [],
      ...(mainSchema !== undefined ? [mainSchema] : []),
      ...this._extenderOptions.jsonAdditionalSchemas,
      ...getRegisteredSchemas(),
    );

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions(
      Object.assign({}, monaco.languages.json.jsonDefaults.diagnosticsOptions, {
        enableSchemaRequest: this._extenderOptions.jsonEnableSchemaRequest,
        schemas: schemas,
        schemaValidation: this._extenderOptions.jsonSchemaSeverity,
        validate: this._extenderOptions.jsonValidate,
      }),
    );

    return options;
  }

  static getDefaultOptions(): IMonacoExtenderOptions {
    return {
      autoResize: true,
      completionProviders: {
        javascript: ["jsBasics"],
      },
      filename: "",
      forceSetup: false,
      jsonAdditionalSchemas: [],
      jsonEnableSchemaRequest: true,
      jsonSchema: undefined,
      jsonValidate: true,
      jsonSchemaSeverity: "warning",
      limitGlobalSuggestions: false,
      placeholder: "",
      types: [],
    };
  }
}

/**
 * A new extender for the monaco editor that customizes the editor further. You should create a
 * new extender for each instance of monaco editor.
 * ```xml
 * <pe:monacoEditor extender="Formcycle.MonacoEditor.createExtender({...})"/>
 * ```
 * @param extenderOptions Optional settings for the extender.
 * @returns The newly created monaco extender for use with the monaco editor
 * component.
 */
export function createExtender(
  extenderOptions?: Partial<IMonacoExtenderOptions>,
): PrimeFaces.widget.ExtMonacoEditor.ExtenderCodeEditor<PrimeFaces.widget.ExtMonacoEditor.SyncMonacoCodeEditorContext> {
  return new MonacoExtender(extenderOptions);
}
